function cart() {
  return {
    // State
    loading: -1,
    checkout: false,

    // Discount state
    discountCode: "",
    discountLoading: false,
    discountError: "",
    discountSuccess: "",
    activeDiscounts: [],
    errorTimeout: null,
    removeTimeout: null,

    // Initialization
    init() {
      this.resetState();
      this.initializeActiveDiscounts();
    },

    resetState() {
      this.checkout = false;
      this.discountCode = "";
      this.discountError = "";
      this.discountSuccess = "";
      this.discountLoading = false;
    },

    // Translation helper
    async setTranslatedError(key) {
      this.discountError = await window.ShopSuey.translations.t(key);
      this.clearErrorWithTimeout();
    },

    async setTranslatedSuccess(key) {
      this.discountSuccess = await window.ShopSuey.translations.t(key);
      window.ShopSuey.cart.createTimeout(() => {
        this.discountSuccess = "";
      });
    },

    // Cart update handlers
    onCartUpdate(detail) {
      if (detail.sections) {
        window.ShopSuey.updateSections(detail, this);
        this.initializeActiveDiscounts();
      } else {
        this.rerenderCart();
      }
    },

    async rerenderCart() {
      try {
        await window.ShopSuey.cart.update({
          url: `${window.routes.cart_url}/update.js`,
          body: {
            sections: window.ShopSuey.cart.getDefaultSections()
          },
          context: this,
          onSuccess: () => {
            this.initializeActiveDiscounts();
          }
        });
      } catch (error) {
        console.error("Error rerendering cart:", error);
      }
    },

    // Discount handling
    clearErrorWithTimeout() {
      if (this.errorTimeout) {
        window.ShopSuey.cart.clearTimeout(this.errorTimeout);
      }
      this.errorTimeout = window.ShopSuey.cart.createTimeout(() => {
        this.discountError = "";
      }, 3000);
    },

    async initializeActiveDiscounts() {
      try {
        await window.ShopSuey.cart.update({
          url: `${window.routes.cart_url}.js`,
          context: this,
          onSuccess: (cartData) => {
            this.activeDiscounts = this.getActiveDiscountsFromCart(cartData);
          },
          onError: () => {
            this.activeDiscounts = [];
          }
        });
      } catch {
        this.activeDiscounts = [];
      }
    },

    getActiveDiscountsFromCart(cartData) {
      const cartLevelDiscounts = (cartData.cart_level_discount_applications || [])
        .map((discount) => discount.code || discount.title)
        .filter(Boolean);

      const lineLevelDiscounts = (cartData.items || [])
        .flatMap((item) =>
          (item.line_level_discount_allocations || []).map((allocation) => {
            const app = allocation.discount_application;
            return app.code || app.title;
          })
        )
        .filter(Boolean);

      return [...new Set([...cartLevelDiscounts, ...lineLevelDiscounts])];
    },

    async isShippingDiscount(code) {
      try {
        const data = await window.ShopSuey.cart.update({
          url: `${window.routes.cart_url}/update.js`,
          body: { discount: code },
          context: this
        });

        const hasShippingDiscount = data.shipping_discount_applications?.length > 0;

        if (hasShippingDiscount) {
          await this.clearDiscount();
        }

        return hasShippingDiscount;
      } catch (error) {
        console.error("Error checking shipping discount:", error);
        return false;
      }
    },

    async clearDiscount() {
      await window.ShopSuey.cart.update({
        url: `${window.routes.cart_url}/update.js`,
        body: { discount: "" },
        context: this
      });
    },

    // Discount actions
    async applyDiscount(event) {
      event.preventDefault();

      if (!this.discountCode?.trim()) {
        await this.setTranslatedError("cart.errors.empty_code");
        return;
      }

      const code = this.discountCode.trim().toUpperCase();
      this.discountLoading = true;
      this.discountError = "";
      this.discountSuccess = "";

      try {
        if (await this.isShippingDiscount(code)) {
          await this.setTranslatedError("cart.errors.shipping_discount");
          return;
        }

        await this.applyDiscountToCart(code);
      } catch (error) {
        console.error("Error applying discount:", error);
        await this.setTranslatedError("cart.errors.invalid_code");
      } finally {
        this.discountLoading = false;
      }
    },

    async applyDiscountToCart(code) {
      await window.ShopSuey.cart.update({
        url: `${window.routes.cart_url}/update.js`,
        body: {
          discount: code,
          sections: window.ShopSuey.cart.getDefaultSections()
        },
        context: this,
        onSuccess: async (response) => {
          if (response.total_price < response.original_total_price) {
            await this.handleSuccessfulDiscount(response);
          } else {
            await this.setTranslatedError("cart.errors.cannot_apply");
          }
        }
      });
    },

    async handleSuccessfulDiscount(response) {
      await this.setTranslatedSuccess("cart.success.discount_applied");
      this.discountCode = "";
      this.activeDiscounts = this.getActiveDiscountsFromCart(response);
    },

    // Quantity updates
    updateQuantity(line, quantity) {
      if (this.removeTimeout) {
        window.ShopSuey.cart.clearTimeout(this.removeTimeout);
      }

      if (quantity <= 0) {
        this.loading = line;
        this.removeTimeout = window.ShopSuey.cart.createTimeout(() => {
          this.performQuantityUpdate(line, 0);
        }, 500);
        return;
      }

      this.performQuantityUpdate(line, quantity);
    },

    async performQuantityUpdate(line, quantity) {
      this.loading = line;

      try {
        // Get the cart item element and scope before making the update
        const cartItem = document.getElementById(`CartItem-${line}`);
        const scope = cartItem ? window.Alpine.$data(cartItem) : null;

        // Use the cart store to update quantity
        const response = await window.Alpine.store("cart").updateQuantity(line, quantity);

        // Handle both error responses and successful responses
        if (response?.errors || response?.status === 422) {
          const errorMessage =
            typeof response.errors === "string"
              ? response.errors
              : Array.isArray(response.errors)
                ? response.errors.join(", ")
                : response.description || window.theme.translations.cart.general.cart_quantity_error;

          if (scope) {
            // Get the current valid quantity from the cart
            const currentQty = await this.getCurrentQuantity(line);

            // Update the scope values sequentially
            scope.qty = currentQty;
            scope.error = errorMessage;

            // Clear error after 5 seconds
            setTimeout(() => {
              if (scope.error === errorMessage) {
                scope.error = "";
              }
            }, 5000);
          }
          return;
        }

        // On success, update sections and clear any error
        if (response?.sections) {
          window.ShopSuey.updateSections({ sections: response.sections }, this);
        }

        if (scope) {
          scope.error = "";
        }

        // If quantity was set to 0, dispatch remove event
        if (quantity === 0) {
          window.dispatchEvent(new CustomEvent("cart-removed"));
        }
      } catch (error) {
        // Only log actual errors, not inventory limit responses
        if (error?.status !== 422) {
          const cartItem = document.getElementById(`CartItem-${line}`);
          const scope = cartItem ? window.Alpine.$data(cartItem) : null;
          if (scope) {
            const errorMessage = window.theme.translations.cart.general.cart_quantity_error;

            scope.error = errorMessage;
            setTimeout(() => {
              if (scope.error === errorMessage) {
                scope.error = "";
              }
            }, 5000);
          }
        }
      } finally {
        this.loading = -1;
        this.removeTimeout = null;
      }
    },

    async getCurrentQuantity(line) {
      try {
        const response = await window.ShopSuey.cart.update({
          url: window.routes.cart_url + ".js"
        });
        return response.items[line - 1]?.quantity || 1;
      } catch (error) {
        console.error("Error getting current quantity:", error);
        return 1;
      }
    },

    // Checkout
    startCheckout() {
      this.checkout = true;
    },

    // Discount removal
    async removeDiscount(code) {
      this.discountLoading = true;
      this.discountError = "";
      this.discountSuccess = "";

      try {
        const remainingDiscounts = this.getRemainingDiscounts(this.activeDiscounts, code);
        await window.ShopSuey.cart.update({
          url: `${window.routes.cart_url}/update.js`,
          body: {
            discount: remainingDiscounts.join(","),
            sections: window.ShopSuey.cart.getDefaultSections()
          },
          context: this,
          onSuccess: async () => {
            await this.handleSuccessfulDiscountRemoval(remainingDiscounts);
          }
        });
      } catch (error) {
        console.error("Error removing discount:", error);
        await this.setTranslatedError("cart.errors.remove_discount");
      } finally {
        this.discountLoading = false;
      }
    },

    getRemainingDiscounts(cartData, codeToRemove) {
      return this.activeDiscounts.filter((code) => code !== codeToRemove);
    },

    async handleSuccessfulDiscountRemoval(remainingDiscounts) {
      await this.setTranslatedSuccess("cart.success.discount_removed");
      this.activeDiscounts = remainingDiscounts;
    }
  };
}

export default cart;
